import { Grid } from '@mui/material';
import { useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import MenuBar, { MenuHeader, MenuSection } from '../../components/new-layout/MenuBar';
import PageLayout from '../../components/new-layout/PageLayout';
import * as routing from '../../constants/Routing';
import ConfirmDialog from '../../dialogs/ConfirmDialog';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import { useRetrieveUserCredentials } from '../../hooks/user/useRetrieveUserCredentials';
import Text from '../../react-components/Text';
import { ProjectSettingsGeneral } from './ProjectSettingsGeneral';
import { ProjectSettingsMembers } from './ProjectSettingsMembers';
import ProjectSettingsOptimizer from './ProjectSettingsOptimizer';
import { ProjectSettingsClients } from './clients';
import { ProjectCustomFields } from './custom_fields/ProjectCustomFields';
import ProjectSettingsPlannerFields from './fields/ProjectSettingsPlannerFields';
import { ProjectSettingsTasksFields } from './fields/ProjectSettingsTasksFields';
import { ProjectSettingsPlans } from './plans/ProjectSettingsPlans';
import { ProjectSettingsSupervisors } from './supervisors';

export const ProjectSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const [dirtyState, setDirtyState] = useState(false);
  const [routeSelected, setRouteSelected] = useState<string | undefined>(undefined);
  const [modalChangesNotSaved, setModalChangesNotSaved] = useState(false);

  const isDirtyState = () => dirtyState;

  const handleDirtyState = (dirtyStateChange: any) => setDirtyState(dirtyStateChange);

  const handleOpenCloseDialog = (newRouteSelected?: string) => {
    setModalChangesNotSaved(!modalChangesNotSaved);
    setRouteSelected(newRouteSelected);
  };

  if (!activeProject) return null;

  const canUpdateProject = userCredentials?.projects.test(activeProject.id, `can_update_project`) ?? false;

  const canViewSupervisors = userCredentials?.projects.test(activeProject.id, `can_view_supervisors`);

  return (
    <>
      <PageLayout>
        <>
          <MenuBar
            content={
              <div>
                <MenuHeader>
                  <Text variant="h6" intlId="project.settings" />
                </MenuHeader>

                <MenuSection
                  id={routing.PROJECT_SETTINGS.GENERAL}
                  active={
                    location.pathname === routing.PROJECT_SETTINGS.GENERAL.replace(`:projectId`, activeProject.id)
                  }
                  textIntlId="projects.settings.general.title"
                  textStyle={{ fontWeight: `bold` }}
                  onClick={() => navigate(routing.PROJECT_SETTINGS.GENERAL.replace(`:projectId`, activeProject.id))}
                />

                {canUpdateProject && (
                  <>
                    <MenuSection
                      id={routing.PROJECT_SETTINGS.PLANNER_FIELDS.ROOT}
                      active={
                        location.pathname ===
                          routing.PROJECT_SETTINGS.PLANNER_FIELDS.CONSTRAINTS.replace(`:projectId`, activeProject.id) ||
                        location.pathname ===
                          routing.PROJECT_SETTINGS.PLANNER_FIELDS.VEHICLES.replace(`:projectId`, activeProject.id) ||
                        location.pathname ===
                          routing.PROJECT_SETTINGS.PLANNER_FIELDS.CLIENTS.replace(`:projectId`, activeProject.id)
                      }
                      textIntlId="projects.settings.planner_fields.title"
                      textStyle={{ fontWeight: `bold` }}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.PROJECT_SETTINGS.PLANNER_FIELDS.CONSTRAINTS);
                        } else {
                          navigate(
                            routing.PROJECT_SETTINGS.PLANNER_FIELDS.CONSTRAINTS.replace(`:projectId`, activeProject.id)
                          );
                        }
                      }}
                    />

                    <MenuSection
                      id={routing.PROJECT_SETTINGS.PLANNER_FIELDS.CONSTRAINTS}
                      active={
                        location.pathname ===
                        routing.PROJECT_SETTINGS.PLANNER_FIELDS.CONSTRAINTS.replace(`:projectId`, activeProject.id)
                      }
                      textIntlId="projects.settings.fields.constraints.title"
                      indent={1}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.PROJECT_SETTINGS.PLANNER_FIELDS.CONSTRAINTS);
                        } else {
                          navigate(
                            routing.PROJECT_SETTINGS.PLANNER_FIELDS.CONSTRAINTS.replace(`:projectId`, activeProject.id)
                          );
                        }
                      }}
                    />

                    <MenuSection
                      id={routing.PROJECT_SETTINGS.PLANNER_FIELDS.VEHICLES}
                      active={
                        location.pathname ===
                        routing.PROJECT_SETTINGS.PLANNER_FIELDS.VEHICLES.replace(`:projectId`, activeProject.id)
                      }
                      textIntlId="projects.settings.fields.vehicle.title"
                      indent={1}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.PROJECT_SETTINGS.PLANNER_FIELDS.VEHICLES);
                        } else {
                          navigate(
                            routing.PROJECT_SETTINGS.PLANNER_FIELDS.VEHICLES.replace(`:projectId`, activeProject.id)
                          );
                        }
                      }}
                    />

                    <MenuSection
                      id={routing.PROJECT_SETTINGS.PLANNER_FIELDS.CLIENTS}
                      active={
                        location.pathname ===
                        routing.PROJECT_SETTINGS.PLANNER_FIELDS.CLIENTS.replace(`:projectId`, activeProject.id)
                      }
                      textIntlId="projects.settings.fields.client.title"
                      indent={1}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.PROJECT_SETTINGS.PLANNER_FIELDS.CLIENTS);
                        } else {
                          navigate(
                            routing.PROJECT_SETTINGS.PLANNER_FIELDS.CLIENTS.replace(`:projectId`, activeProject.id)
                          );
                        }
                      }}
                    />

                    {userCredentials?.subscription.test(`planner.tasks`) && activeProject.tasks_enabled && (
                      <MenuSection
                        id={routing.PROJECT_SETTINGS.TASKS_FIELDS}
                        active={
                          location.pathname ===
                          routing.PROJECT_SETTINGS.TASKS_FIELDS.replace(`:projectId`, activeProject.id)
                        }
                        textIntlId="projects.settings.fields.tasks.title"
                        textStyle={{ fontWeight: `bold` }}
                        onClick={() => {
                          if (isDirtyState()) {
                            handleOpenCloseDialog(routing.PROJECT_SETTINGS.TASKS_FIELDS);
                          } else {
                            navigate(routing.PROJECT_SETTINGS.TASKS_FIELDS.replace(`:projectId`, activeProject.id));
                          }
                        }}
                      />
                    )}
                  </>
                )}

                <MenuSection
                  id={routing.PROJECT_SETTINGS.CUSTOM_FIELDS}
                  active={
                    location.pathname === routing.PROJECT_SETTINGS.CUSTOM_FIELDS.replace(`:projectId`, activeProject.id)
                  }
                  textIntlId="projects.settings.fields.custom_fields"
                  textStyle={{ fontWeight: `bold` }}
                  onClick={() => {
                    if (isDirtyState()) {
                      handleOpenCloseDialog(routing.PROJECT_SETTINGS.CUSTOM_FIELDS);
                    } else {
                      navigate(routing.PROJECT_SETTINGS.CUSTOM_FIELDS.replace(`:projectId`, activeProject.id));
                    }
                  }}
                />

                <MenuSection
                  id={routing.PROJECT_SETTINGS.MEMBERS}
                  active={
                    location.pathname === routing.PROJECT_SETTINGS.MEMBERS.replace(`:projectId`, activeProject.id)
                  }
                  textIntlId="projects.settings.members.title"
                  textStyle={{ fontWeight: `bold` }}
                  onClick={() => {
                    if (isDirtyState()) {
                      handleOpenCloseDialog(routing.PROJECT_SETTINGS.MEMBERS);
                    } else {
                      navigate(routing.PROJECT_SETTINGS.MEMBERS.replace(`:projectId`, activeProject.id));
                    }
                  }}
                />

                {canUpdateProject && (
                  <MenuSection
                    id={routing.PROJECT_SETTINGS.OPTIMIZER}
                    active={
                      location.pathname === routing.PROJECT_SETTINGS.OPTIMIZER.replace(`:projectId`, activeProject.id)
                    }
                    textIntlId="projects.settings.optimizer.title"
                    textStyle={{ fontWeight: `bold` }}
                    onClick={() => {
                      if (isDirtyState()) {
                        handleOpenCloseDialog(routing.PROJECT_SETTINGS.OPTIMIZER);
                      } else {
                        navigate(routing.PROJECT_SETTINGS.OPTIMIZER.replace(`:projectId`, activeProject.id));
                      }
                    }}
                  />
                )}

                <MenuSection
                  id={routing.PROJECT_SETTINGS.PLANS}
                  active={location.pathname === routing.PROJECT_SETTINGS.PLANS.replace(`:projectId`, activeProject.id)}
                  textIntlId="projects.settings.plans.title"
                  textStyle={{ fontWeight: `bold` }}
                  onClick={() => {
                    if (isDirtyState()) {
                      handleOpenCloseDialog(routing.PROJECT_SETTINGS.PLANS);
                    } else {
                      navigate(routing.PROJECT_SETTINGS.PLANS.replace(`:projectId`, activeProject.id));
                    }
                  }}
                />

                <MenuSection
                  id={routing.PROJECT_SETTINGS.CLIENTS.ROOT}
                  active={
                    location.pathname ===
                      routing.PROJECT_SETTINGS.CLIENTS.LIST.replace(`:projectId`, activeProject.id) ||
                    location.pathname === routing.PROJECT_SETTINGS.CLIENTS.MAP.replace(`:projectId`, activeProject.id)
                  }
                  textIntlId="project.assets.clients"
                  textStyle={{ fontWeight: `bold` }}
                  onClick={() => {
                    if (isDirtyState()) {
                      handleOpenCloseDialog(routing.PROJECT_SETTINGS.CLIENTS.ROOT);
                    } else {
                      navigate(routing.PROJECT_SETTINGS.CLIENTS.ROOT.replace(`:projectId`, activeProject.id));
                    }
                  }}
                />

                {canViewSupervisors ? (
                  <>
                    <MenuSection
                      id={routing.PROJECT_SETTINGS.SUPERVISORS.ROOT}
                      active={
                        location.pathname ===
                          routing.PROJECT_SETTINGS.SUPERVISORS.LIST.replace(`:projectId`, activeProject.id) ||
                        location.pathname ===
                          routing.PROJECT_SETTINGS.SUPERVISORS.STYLING.replace(`:projectId`, activeProject.id)
                      }
                      textIntlId="supervisors.menubar.title"
                      textStyle={{ fontWeight: `bold` }}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.PROJECT_SETTINGS.SUPERVISORS.ROOT);
                        } else {
                          navigate(routing.PROJECT_SETTINGS.SUPERVISORS.ROOT.replace(`:projectId`, activeProject.id));
                        }
                      }}
                    />

                    <MenuSection
                      id={routing.PROJECT_SETTINGS.SUPERVISORS.LIST}
                      active={
                        location.pathname ===
                        routing.PROJECT_SETTINGS.SUPERVISORS.LIST.replace(`:projectId`, activeProject.id)
                      }
                      textIntlId="supervisors.menubar.list"
                      indent={1}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.PROJECT_SETTINGS.SUPERVISORS.LIST);
                        } else {
                          navigate(routing.PROJECT_SETTINGS.SUPERVISORS.LIST.replace(`:projectId`, activeProject.id));
                        }
                      }}
                    />

                    <MenuSection
                      id={routing.PROJECT_SETTINGS.SUPERVISORS.STYLING}
                      active={
                        location.pathname ===
                        routing.PROJECT_SETTINGS.SUPERVISORS.STYLING.replace(`:projectId`, activeProject.id)
                      }
                      textIntlId="supervisors.menubar.styling"
                      indent={1}
                      onClick={() => {
                        if (isDirtyState()) {
                          handleOpenCloseDialog(routing.PROJECT_SETTINGS.SUPERVISORS.STYLING);
                        } else {
                          navigate(
                            routing.PROJECT_SETTINGS.SUPERVISORS.STYLING.replace(`:projectId`, activeProject.id)
                          );
                        }
                      }}
                    />
                  </>
                ) : null}
              </div>
            }
          />

          <Grid
            container
            direction="column"
            wrap="nowrap"
            style={{ backgroundColor: `white`, zIndex: 2, height: `100%`, overflowY: `auto` }}
          >
            <Routes>
              <Route path="/general" element={<ProjectSettingsGeneral project={activeProject} />} />

              {canUpdateProject && (
                <>
                  <Route path="/planner-fields/*" element={<ProjectSettingsPlannerFields project={activeProject} />} />
                  {userCredentials?.subscription.test(`planner.tasks`) && activeProject.tasks_enabled && (
                    <Route path="/tasks-fields/*" element={<ProjectSettingsTasksFields project={activeProject} />} />
                  )}
                </>
              )}

              <Route path="/custom-fields" element={<ProjectCustomFields />} />

              <Route path="/members" element={<ProjectSettingsMembers activeProject={activeProject} />} />

              {canUpdateProject && (
                <Route
                  path="/optimizer"
                  element={<ProjectSettingsOptimizer project={activeProject} isDirtyState={handleDirtyState} />}
                />
              )}

              <Route
                path="/plans"
                element={<ProjectSettingsPlans project={activeProject} isDirtyState={handleDirtyState} />}
              />

              <Route path="/clients/*" element={<ProjectSettingsClients />} />

              {canViewSupervisors && <Route path="/supervisors/*" element={<ProjectSettingsSupervisors />} />}

              <Route
                path="*"
                element={
                  <Navigate to={routing.PROJECT_SETTINGS.GENERAL.replace(`:projectId`, `${activeProject?.id}`)} />
                }
              />
            </Routes>
          </Grid>
        </>
      </PageLayout>

      <ConfirmDialog
        open={dirtyState && modalChangesNotSaved}
        onCloseDialog={() => handleOpenCloseDialog()}
        title={<Text variant="h6" intlId="settings.notsaved.title" />}
        description={<Text intlId="settings.notsaved.subtitle" />}
        cancelIntlId="settings.notsaved.stayonpage"
        confirmIntlId="settings.notsaved.leavethepage"
        onConfirm={() => {
          if (routeSelected) {
            navigate(routeSelected);
            handleOpenCloseDialog();
            handleDirtyState(false);
          }
        }}
      />
    </>
  );
};
